import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ParentNodeTreePicker from "common/Components/ParentNodeTreePicker/ParentNodeTreePicker";
import { GenericForm } from "common/Components/GenericForm";
import { SideDrawerLayout, SIDE_DRAWER_UI } from "common/Components/SideDrawer";
import {
  isFiniteNumber,
  isNullUndefinedOrEmptyString,
} from "common/utils/valueChecks";
import {
  NODE_NAME_MAX_LENGTH,
  NODE_NAME_MESSAGES,
  nodeNameFormatError,
  validateNodeNameForSave,
} from "common/utils/nodeNameValidation";
import {
  filterNodeTypeOptionsForTagEdit,
  getRowSourceConnector,
  isNodeTypeLockedForSourceTag,
  isWmAppConnector,
  resolveNodeTypeForTagEdit,
} from "./nodeListRowsMapper";
import { fetchContextNodeForTagRow } from "./nodeListApi";
import { TAGS_UI } from "./NodeList.constants";
import {
  DetailsReadOnlyStack,
  FieldsStack,
  InstructionText,
  ReadOnlyFieldCopyButton,
  ReadOnlyFieldLabel,
  ReadOnlyFieldMain,
  ReadOnlyFieldRow,
  ReadOnlyFieldValue,
  SectionBlock,
  SectionHeading,
} from "./NodeListDetailsDrawer.styles";

const optionShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
});

function getTagNodeTypeOption(nodeTypeOptions) {
  const fromReference = (nodeTypeOptions ?? []).find(
    (option) => String(option?.value ?? "").toLowerCase() === "tag"
  );
  return fromReference ?? { value: "tag", label: "Tag" };
}

function createEmptyForm() {
  return {
    nodeType: "",
    nodeSubType: "",
    parentNode: "",
    parentNodeId: null,
    parentNodeName: "",
    nodeName: "",
    sourceConnector: "",
    sourcePath: "",
  };
}

function formFromTagsRow(row) {
  return {
    nodeType: resolveNodeTypeForTagEdit(row),
    nodeSubType: String(row.nodeSubType ?? ""),
    parentNode:
      row.parentNode != null && row.parentNode !== ""
        ? String(row.parentNode)
        : "",
    parentNodeId: isFiniteNumber(row.parentNodeId)
      ? Number(row.parentNodeId)
      : null,
    parentNodeName:
      row.parentNodeName != null && String(row.parentNodeName).trim() !== ""
        ? String(row.parentNodeName).trim()
        : "",
    nodeName: "",
    sourceConnector: String(
      row.sourceConnector ?? row.sourceSystem ?? row.source ?? ""
    ),
    sourcePath: String(row.sourceTag ?? ""),
  };
}

function formFromContextNodeDto(dto, row) {
  const parentNodeId = isFiniteNumber(dto?.parentNodeId)
    ? Number(dto.parentNodeId)
    : null;
  return {
    nodeType: resolveNodeTypeForTagEdit({
      ...row,
      nodeType: dto?.nodeType ?? row?.nodeType ?? "",
    }),
    nodeSubType: String(
      dto?.nodeSubType ?? dto?.nodeSubtype ?? row?.nodeSubType ?? ""
    ),
    parentNode: parentNodeId != null ? String(parentNodeId) : "",
    parentNodeId,
    parentNodeName:
      dto?.parentNodeName != null && String(dto.parentNodeName).trim() !== ""
        ? String(dto.parentNodeName).trim()
        : "",
    nodeName: String(dto?.nodeName ?? ""),
    sourceConnector: String(
      row?.sourceConnector ?? row?.sourceSystem ?? row?.source ?? ""
    ),
    sourcePath: String(row?.sourceTag ?? ""),
  };
}

function readOnlyRow({ label, value, onCopy }) {
  const display =
    isNullUndefinedOrEmptyString(value) ? "—" : String(value);
  return (
    <ReadOnlyFieldRow>
      <ReadOnlyFieldMain>
        <ReadOnlyFieldLabel component="div">{label}</ReadOnlyFieldLabel>
        <ReadOnlyFieldValue variant="body2" component="div" title={display}>
          {display}
        </ReadOnlyFieldValue>
      </ReadOnlyFieldMain>
      {display !== "—" ? (
        <ReadOnlyFieldCopyButton>
          <Tooltip title="Copy">
            <IconButton
              size="small"
              aria-label={`Copy ${label}`}
              onClick={() => onCopy(display)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </ReadOnlyFieldCopyButton>
      ) : null}
    </ReadOnlyFieldRow>
  );
}

export default function NodeListDetailsDrawer({
  open,
  row,
  onClose,
  onSave,
  saving,
  nodeTypeOptions,
  subtypeOptionsByNodeType,
  referencePayload,
  parentCandidates,
}) {
  const [form, setForm] = useState(createEmptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [resolvedContextNodeId, setResolvedContextNodeId] = useState(null);

  const isUncontextualized = Boolean(row?.uncontextualized);
  const contextualizeNodeTypeOption = useMemo(
    () => getTagNodeTypeOption(nodeTypeOptions),
    [nodeTypeOptions]
  );

  const subtypeSelectOptions = useMemo(
    () =>
      form.nodeType
        ? subtypeOptionsByNodeType[String(form.nodeType)] ?? []
        : [],
    [form.nodeType, subtypeOptionsByNodeType]
  );

  const sourceConnector = getRowSourceConnector(row);
  const editableNodeTypeOptions = useMemo(
    () => filterNodeTypeOptionsForTagEdit(nodeTypeOptions, sourceConnector),
    [nodeTypeOptions, sourceConnector]
  );

  useEffect(() => {
    if (!open || !row) {
      setForm(createEmptyForm());
      setFieldErrors({});
      setResolvedContextNodeId(null);
      return;
    }
    if (row.uncontextualized) {
      setForm({
        nodeType: String(contextualizeNodeTypeOption.value),
        nodeSubType: "",
        parentNode: "",
        parentNodeId: null,
        parentNodeName: "",
        nodeName: "",
        sourceConnector: String(
          row.sourceConnector ?? row.sourceSystem ?? row.source ?? ""
        ),
        sourcePath: String(row.sourceTag ?? ""),
      });
      setFieldErrors({});
      setResolvedContextNodeId(null);
      return;
    }

    setForm(formFromTagsRow(row));
    setFieldErrors({});
    setResolvedContextNodeId(null);

    let cancelled = false;
    (async () => {
      const result = await fetchContextNodeForTagRow(row);
      if (cancelled || result?.error || !result?.data) {
        return;
      }
      const nodeId = result.data.nodeId;
      if (isFiniteNumber(nodeId)) {
        setResolvedContextNodeId(Number(nodeId));
      }
      setForm(formFromContextNodeDto(result.data, row));
    })();

    return () => {
      cancelled = true;
    };
  }, [open, row, contextualizeNodeTypeOption.value]);

  const mergeForm = (patch) => setForm((previous) => ({ ...previous, ...patch }));

  const handleCopy = (text) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  };

  const handleSave = async () => {
    setFieldErrors({});
    if (!row) return;

    if (isUncontextualized) {
      const nextErrors = {};
      const nodeNameError = validateNodeNameForSave(form.nodeName);
      if (nodeNameError) {
        nextErrors.nodeName = nodeNameError;
      }
      if (!(form.nodeType ?? "").trim()) {
        nextErrors.nodeType = TAGS_UI.validation.nodeTypeRequired;
      }
      if (form.parentNodeId == null || form.parentNodeId === "") {
        nextErrors.parentNode = TAGS_UI.validation.parentRequired;
      }
      if (Object.keys(nextErrors).length) {
        setFieldErrors(nextErrors);
        return;
      }
      const patch = {
        nodeType: String(contextualizeNodeTypeOption.value),
        nodeName: (form.nodeName ?? "").trim(),
        parentNodeId: form.parentNodeId,
      };
      if ((form.nodeSubType ?? "").trim()) {
        patch.nodeSubType = form.nodeSubType.trim();
      }
      await onSave?.(patch);
      return;
    }

    const nextErrors = {};
    if (!(form.nodeType ?? "").trim()) {
      nextErrors.nodeType = TAGS_UI.validation.nodeTypeRequired;
    }
    const nodeNameError = validateNodeNameForSave(form.nodeName);
    if (nodeNameError) {
      nextErrors.nodeName = nodeNameError;
    }
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }
    const patch = {
      nodeType: form.nodeType.trim(),
      nodeName: form.nodeName.trim(),
    };
    if ((form.nodeSubType ?? "").trim()) {
      patch.nodeSubType = form.nodeSubType.trim();
    }
    if (form.parentNodeId != null && form.parentNodeId !== "") {
      patch.parentNodeId = form.parentNodeId;
    }
    await onSave?.(patch, resolvedContextNodeId);
  };
  const selectedLabel = row
    ? row.sourceTag || row.nodeName || row.rowKey || ""
    : "";

  const sourceNameDisplay =
    row?.sourceConnector ?? row?.sourceSystem ?? "";

  const nodeNameLiveError =
    nodeNameFormatError(form.nodeName) ||
    (form.nodeName.length >= NODE_NAME_MAX_LENGTH
      ? NODE_NAME_MESSAGES.MAX_LENGTH
      : "");

  const isNodeTypeDisabled = isNodeTypeLockedForSourceTag(row);
  const isTagOnlyNodeTypeSelection =
    isUncontextualized ||
    (isNodeTypeDisabled && !isWmAppConnector(sourceConnector));
  const nodeTypeOptionsForSelect = isTagOnlyNodeTypeSelection
    ? [contextualizeNodeTypeOption]
    : editableNodeTypeOptions;
  const nodeSubtypePlaceholder = !form.nodeType
    ? "Select a Node Type"
    : !subtypeSelectOptions.length
      ? "No Subtypes for this Type"
      : "Node Subtype";

  const editableFormFields = useMemo(
    () => [
      {
        name: "nodeName",
        type: "text",
        label: "Name",
        hint: "The name this node carries in the context hierarchy.",
        validation: { maxLength: NODE_NAME_MAX_LENGTH },
        inputProps: { "aria-label": "Name" },
      },
      {
        name: "nodeType",
        type: "singleSelect",
        label: "Type",
        options: nodeTypeOptionsForSelect,
        disabled: isNodeTypeDisabled,
        disableClearable: true,
        inputProps: { "aria-label": "Type" },
      },
      {
        name: "nodeSubType",
        type: "singleSelect",
        label: "Subtype",
        placeholder: nodeSubtypePlaceholder,
        options: subtypeSelectOptions,
        disabled: !form.nodeType,
        inputProps: { "aria-label": "Subtype" },
      },
      {
        name: "parentNode",
        type: "custom",
        customRender: (_value, _onChange, _field, error) => (
          <ParentNodeTreePicker
            label="Parent"
            hint="Clear it to leave the node uncontextualized."
            nodeType={form.nodeType}
            referencePayload={referencePayload}
            parentCandidates={parentCandidates}
            disabled={!form.nodeType}
            valueId={form.parentNodeId}
            displayNameOverride={
              form.parentNodeName != null &&
              String(form.parentNodeName).trim() !== ""
                ? String(form.parentNodeName).trim()
                : undefined
            }
            onChange={(patch) => {
              mergeForm(patch);
              if (fieldErrors.parentNode) {
                setFieldErrors((previous) => {
                  const next = { ...previous };
                  delete next.parentNode;
                  return next;
                });
              }
            }}
            error={error}
            inputTestId="tag-details-parent-picker"
            placeholder="None"
          />
        ),
      },
    ],
    [
      fieldErrors.parentNode,
      form.nodeType,
      form.parentNodeId,
      form.parentNodeName,
      isNodeTypeDisabled,
      isUncontextualized,
      nodeSubtypePlaceholder,
      nodeTypeOptionsForSelect,
      parentCandidates,
      referencePayload,
      subtypeSelectOptions,
    ]
  );

  const handleEditableFormChange = (name, value) => {
    if (name === "nodeType") {
      mergeForm({
        nodeType: value ?? "",
        nodeSubType: "",
        parentNode: "",
        parentNodeId: null,
      });
      setFieldErrors((previous) => {
        const next = { ...previous };
        delete next.nodeType;
        delete next.parentNode;
        return next;
      });
      return;
    }
    if (name === "nodeSubType") {
      mergeForm({ nodeSubType: value ?? "" });
      return;
    }
    if (name === "nodeName") {
      mergeForm({ nodeName: value });
      if (
        fieldErrors.nodeName === NODE_NAME_MESSAGES.MAX_LENGTH ||
        fieldErrors.nodeName === NODE_NAME_MESSAGES.NO_SPECIAL_CHARS ||
        fieldErrors.nodeName === NODE_NAME_MESSAGES.REQUIRED
      ) {
        setFieldErrors((previous) => {
          const next = { ...previous };
          delete next.nodeName;
          return next;
        });
      }
    }
  };

  return (
    <SideDrawerLayout
      open={open}
      onClose={onClose}
      title="Node details"
      description={row ? `Selected: ${selectedLabel}` : undefined}
      onSubmit={handleSave}
      submitLabel="Save changes"
      cancelLabel={SIDE_DRAWER_UI.cancelLabel}
      submitting={saving}
      submitDisabled={!row}
      cancelDisabled={saving}
      submitTestId="tag-details-save"
    >
      {row ? (
        <>
          <SectionBlock>
            <SectionHeading component="div">Details</SectionHeading>
            <DetailsReadOnlyStack>
              {readOnlyRow({
                label: "Connector",
                value: sourceNameDisplay,
                onCopy: handleCopy,
              })}
              {readOnlyRow({
                label: "Source path",
                value: row.sourceTag,
                onCopy: handleCopy,
              })}
              {readOnlyRow({
                label: "Context path",
                value: row?.uncontextualized ? "—" : (row.contextTag || row.context),
                onCopy: handleCopy,
              })}
            </DetailsReadOnlyStack>
          </SectionBlock>

          <SectionBlock>
            <SectionHeading component="div">Edit node context</SectionHeading>
            <InstructionText component="div">
              {isUncontextualized
                ? "Set a name, type, subtype and parent to contextualize this node. A node is contextualized once it has a valid type and a contextualized parent."
                : "Update node type, subtype, parent, and name. Leave subtype empty if not applicable."}
            </InstructionText>

            <GenericForm
              sections={[
                {
                  fields: editableFormFields,
                  wrapper: FieldsStack,
                },
              ]}
              values={form}
              onChange={handleEditableFormChange}
              errors={{
                ...fieldErrors,
                ...(nodeNameLiveError ? { nodeName: nodeNameLiveError } : {}),
              }}
              showBorder={false}
            />
          </SectionBlock>
        </>
      ) : null}
    </SideDrawerLayout>
  );
}

NodeListDetailsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  nodeTypeOptions: PropTypes.arrayOf(optionShape).isRequired,
  subtypeOptionsByNodeType: PropTypes.objectOf(PropTypes.arrayOf(optionShape)),
  referencePayload: PropTypes.object,
  parentCandidates: PropTypes.arrayOf(PropTypes.object),
};

NodeListDetailsDrawer.defaultProps = {
  row: null,
  saving: false,
  subtypeOptionsByNodeType: {},
  referencePayload: null,
  parentCandidates: [],
};
